import history from 'BrowserHistory';

/**
 * The main application routes.
 */
export const AppRoutes = {
  ROOT: '/(clusters/)?:customerUrlSlug?/:organizationUrlSlug?/:accountUrlSlug?',
  CLUSTER_DASHBOARD: `/clusters/:customerUrlSlug?/:organizationUrlSlug?/:accountUrlSlug?/dashboard`,
  CLUSTER_ADMIN: '/clusters',
  CREATE_CLUSTER: '/clusters/accounts/create',
  RECREATE_CLUSTER: '/clusters/accounts/recreate',
  MY_PROFILE: '/profile',
  TOS: '/tos',
  AUTHORIZATION_ERROR: '/authorization-error',
  TASK: '/tasks/:taskId',
};

Object.freeze(AppRoutes);

export type DashboardRouteParams = {
  customerUrlSlug: string;
  organizationUrlSlug: string;
  accountUrlSlug: string;
};

/**
 * The routes on the dashboard page.
 */
export const ClusterDashboardRoutes = {
  SUMMARY: 'summary',
  HARDWARE: 'hardware',
  AUDIT_TRAIL: 'audit-trail',
  CLOUD_RESOURCES: 'cloud-resources',
  FLOW_GATEWAY: 'flow-gateway',
  NETWORK: 'network',
  SETTINGS: 'settings',
  NOTIFICATIONS: 'notifications',
  TASKS: 'tasks',
  USERS: 'users',
};

Object.freeze(ClusterDashboardRoutes);

/**
 * The routes on the Admin page.
 */
export const AdminRoutes = {
  ACCOUNTS: 'accounts',
  ACCOUNT: 'account',
  ORGANIZATIONS: 'organizations',
  ORGANIZATION: 'organization',
  CUSTOMERS: 'customers',
  CUSTOMER: 'customer',
};

Object.freeze(AdminRoutes);

/**
 * The clusters list, used as a fallback by the pages which are opened from it
 * but can also be opened directly.
 */
export const CLUSTERS_LIST_ROUTE = `${AppRoutes.CLUSTER_ADMIN}/${AdminRoutes.ACCOUNTS}/list`;

const BACK_ROUTE_PARAM = 'url';

type DialogRouteParams = Record<string, string | undefined>;

function createDialogPageLink(route: string, backRoute?: string, params: DialogRouteParams = {}) {
  const searchParams = new URLSearchParams();

  if (backRoute) {
    searchParams.set(BACK_ROUTE_PARAM, backRoute);
  }

  Object.entries(params).forEach(([name, value]) => {
    if (value) {
      searchParams.set(name, value);
    }
  });

  return `${route}?${searchParams.toString()}`;
}

type CreateClusterLinkProps = {
  backRoute?: string;
  organizationId?: string;
};

export function createClusterLink({ backRoute, organizationId }: CreateClusterLinkProps) {
  return createDialogPageLink(AppRoutes.CREATE_CLUSTER, backRoute, { organizationId });
}

type RecreateClusterLinkProps = {
  backRoute?: string;
  clusterId: string;
};

export function recreateClusterLink({ backRoute, clusterId }: RecreateClusterLinkProps) {
  return `${createDialogPageLink(AppRoutes.RECREATE_CLUSTER, backRoute, { clusterId })}#main-content`;
}

export function goTo(link: string) {
  history.push(link);
}

export function goBack(fallbackRoute: string = CLUSTERS_LIST_ROUTE, search: string = history.location.search) {
  const backRoute = new URLSearchParams(search).get(BACK_ROUTE_PARAM);
  history.push(`${backRoute || fallbackRoute}#main-content`);
}

/**
 * The routes on the Profile page.
 */
export const ProfileRoutes = {
  GENERAL: 'general',
  ADVANCED: 'advanced',
  TASKS: 'tasks',
  NOTIFICATIONS: 'notifications',
  SUBSCRIPTIONS: 'subscriptions',
  SITE_MAP: 'site-map',
};

Object.freeze(ProfileRoutes);

export const ProfileRouteTemplates = {
  GENERAL: [AppRoutes.MY_PROFILE, ProfileRoutes.GENERAL].join('/'),
  ADVANCED: [AppRoutes.MY_PROFILE, ProfileRoutes.ADVANCED].join('/'),
  NOTIFICATIONS: [AppRoutes.MY_PROFILE, ProfileRoutes.NOTIFICATIONS].join('/'),
  TASKS: [AppRoutes.MY_PROFILE, ProfileRoutes.TASKS].join('/'),
  SUBSCRIPTIONS: [AppRoutes.MY_PROFILE, ProfileRoutes.SUBSCRIPTIONS].join('/'),
  SITE_MAP: [AppRoutes.MY_PROFILE, ProfileRoutes.SITE_MAP].join('/'),
};

Object.freeze(ProfileRouteTemplates);

export const SidebarRoutesTemplates = {
  ACCOUNT: [AppRoutes.CLUSTER_ADMIN, AdminRoutes.ACCOUNT].join('/'),
  ACCOUNTS: [AppRoutes.CLUSTER_ADMIN, AdminRoutes.ACCOUNTS].join('/'),
  ORGANIZATION: [AppRoutes.CLUSTER_ADMIN, AdminRoutes.ORGANIZATION].join('/'),
  ORGANIZATIONS: [AppRoutes.CLUSTER_ADMIN, AdminRoutes.ORGANIZATIONS].join('/'),
  CUSTOMER: [AppRoutes.CLUSTER_ADMIN, AdminRoutes.CUSTOMER].join('/'),
  CUSTOMERS: [AppRoutes.CLUSTER_ADMIN, AdminRoutes.CUSTOMERS].join('/'),

  NOTIFICATIONS: [AppRoutes.MY_PROFILE, ProfileRoutes.NOTIFICATIONS].join('/'),
  TASKS: [AppRoutes.MY_PROFILE, ProfileRoutes.TASKS].join('/'),
  SUBSCRIPTIONS: [AppRoutes.MY_PROFILE, ProfileRoutes.SUBSCRIPTIONS].join('/'),
};

Object.freeze(SidebarRoutesTemplates);

/**
 * Create route from provided route template and params
 * If params are not provided route parts are changed for entities they represent
 * for example:
 *  :customerUrlSlug will become customer
 *  :accountUrlSlug will become account
 *  etc
 *
 * @param routeTemplate
 * @param params
 */
export function createRouteFromParams(routeTemplate: string, params: { [key: string]: unknown } = {}) {
  const parts = routeTemplate.split('/');

  return parts
    .map(part => {
      /**
       * If part is starting with :
       * that means it's a param
       * so we remove : at the start and ? at the end of param name
       */
      if (part.charAt(0) === ':') {
        const partName = part.replace(/^:|\?$/g, '');
        return params[partName];
      }

      return part;
    })
    .join('/')
    .replace(/\/+/g, '/');
}
